import { enrollExam, getUserInfo, getExamBundle } from "aws/lambda";
import React from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { getExamById } from "aws/dynamodb";
import {
  refactorExamList,
  synchronizeFetchExams,
  addEnrolledExam,
} from "store/ExamStore/slice";
import { useDispatch } from "react-redux";
import { useAuth } from "aws/AuthContext";
import { TypeExam } from "app/constants/types/exam.types";

// Define bundle info interface to match the Lambda function response
interface BundleInfo {
  examId: string;
  bundleId: string;
  bundleTitle: string;
  level: string;
  quotasRequired: number;
  exams: string[]; // Array of exam IDs, not full exam objects
  processedExams?: TypeExam[]; // We'll populate this after fetching the full exam details
}

type Props = {};

// Define user info interface based on the Lambda function response
interface UserInfo {
  cognitoUsername: string;
  purchasedHLQuantity: number;
  purchasedSLQuantity: number;
  enrolledHLQuantity: number;
  enrolledSLQuantity: number;
  updatedAt: number;
}

const useCheckoutPage = (props: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { examId } = useParams(); // Get examId from URL slug

  const [exam, setExam] = React.useState<TypeExam | undefined>(); // Initialize data as null
  const [userInfo, setUserInfo] = React.useState<UserInfo | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [bundleInfo, setBundleInfo] = React.useState<BundleInfo | null>(null);

  // Calculate available quotas
  const hlQuota = userInfo
    ? Math.max(0, userInfo.purchasedHLQuantity - userInfo.enrolledHLQuantity)
    : 0;
  const slQuota = userInfo
    ? Math.max(0, userInfo.purchasedSLQuantity - userInfo.enrolledSLQuantity)
    : 0;

  // Determine if the current exam level is HL or SL and check available quota
  const examLevel = exam?.level?.toLowerCase() || "";
  const isHL = examLevel.includes("higher") || examLevel.includes("hl");
  const isSL = examLevel.includes("standard") || examLevel.includes("sl");

  // Check if user has enough quota for this exam or bundle
  const quotasRequired = bundleInfo ? bundleInfo.quotasRequired : 1;

  // Debug logging to help diagnose quota issues
  console.log("Quota check:", {
    isHL,
    isSL,
    hlQuota,
    slQuota,
    quotasRequired,
    bundleLevel: bundleInfo?.level,
  });

  // If bundle has a specific level, use that for quota check instead of the exam level
  const bundleLevel = bundleInfo?.level?.toLowerCase() || "";
  const isBundleHL =
    bundleLevel.includes("higher") || bundleLevel.includes("hl");
  const isBundleSL =
    bundleLevel.includes("standard") || bundleLevel.includes("sl");

  // Use bundle level if available, otherwise use exam level
  const checkHL = bundleInfo ? isBundleHL : isHL;
  const checkSL = bundleInfo ? isBundleSL : isSL;

  const hasQuota = checkHL
    ? hlQuota >= quotasRequired
    : checkSL
    ? slQuota >= quotasRequired
    : false;

  const handleBuy = async () => {
    if (!hasQuota) {
      // Determine the quota type based on the bundle level if available
      const quotaType = bundleInfo
        ? isBundleHL
          ? "HL"
          : "SL"
        : isHL
        ? "HL"
        : "SL";
      const bundleMessage = bundleInfo
        ? ` for the ${bundleInfo.level} bundle (requires ${quotasRequired} quota)`
        : "";
      toast.error(
        `You don't have enough ${quotaType} quota${bundleMessage}. Please purchase more.`
      );
      return;
    }

    if (examId) {
      try {
        // Call the updated enrollExam function which uses auth token for userId
        const response = await enrollExam(examId);

        if (response && !response.error) {
          // Directly update Redux store with new enrollment
          if (user?.userId) {
            dispatch(
              addEnrolledExam({
                userId: user.userId,
                examId: examId,
              })
            );
          }

          // Synchronize all exam data
          await dispatch(synchronizeFetchExams());

          const successMessage = bundleInfo
            ? `Successfully enrolled in the ${bundleInfo.bundleTitle} bundle with ${bundleInfo.exams.length} exams!`
            : "Successfully enrolled in exam!";
          toast.success(successMessage);
          navigate("/exam");
        } else {
          toast.error(response?.error || "Failed to enroll in exam");
        }
      } catch (error) {
        console.error("Error enrolling in exam:", error);

        // Handle authentication errors specifically
        if (
          error instanceof Error &&
          error.message.includes("Authentication")
        ) {
          toast.error("Authentication required. Please sign in again.");
        } else {
          toast.error(
            `Enrollment failed: ${
              error instanceof Error ? error.message : "Unknown error"
            }`
          );
        }
      }
    }
  };

  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        // Fetch exam details
        if (examId) {
          const response = await getExamById(examId);
          if (response) {
            const examData = await refactorExamList([response]);
            setExam(examData[0]);

            // Fetch bundle information if available
            try {
              const bundleResponse = await getExamBundle(examId);
              if (bundleResponse && !bundleResponse.error) {
                console.log(
                  "Bundle information fetched successfully",
                  bundleResponse
                );

                // Store the bundle info
                setBundleInfo(bundleResponse);

                // Fetch full exam details for each exam ID in the bundle
                if (bundleResponse.exams && bundleResponse.exams.length > 0) {
                  console.log("Fetching full exam details for bundle exams...");

                  // Create an array of promises to fetch each exam
                  const examPromises = bundleResponse.exams.map(
                    async (examId: string) => {
                      try {
                        const examResponse = await getExamById(examId);
                        return examResponse;
                      } catch (error) {
                        console.error(`Error fetching exam ${examId}:`, error);
                        return null;
                      }
                    }
                  );

                  // Wait for all exam fetches to complete
                  const examResults = await Promise.all(examPromises);

                  // Filter out any null results and process the exams
                  const validExams = examResults.filter(
                    (exam) => exam !== null
                  );
                  if (validExams.length > 0) {
                    const processedExams = await refactorExamList(validExams);

                    // Update the bundle info with the processed exams
                    setBundleInfo((prevState) => {
                      if (prevState) {
                        return {
                          ...prevState,
                          processedExams: processedExams,
                        };
                      }
                      return prevState;
                    });

                    console.log(
                      "Bundle exams processed:",
                      processedExams.length
                    );
                  }
                }
              }
            } catch (bundleError) {
              console.error("Error fetching bundle info:", bundleError);
              // Not a critical error, so we continue without bundle info
            }
          }
        }

        // Fetch user info for quota
        const userInfoResponse = await getUserInfo();
        setUserInfo(userInfoResponse);
        console.log("User info fetched successfully", userInfoResponse);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [examId]);

  return {
    exam,
    handleBuy,
    loading,
    userInfo,
    hlQuota,
    slQuota,
    hasQuota,
    isHL,
    isSL,
    bundleInfo,
    quotasRequired,
  };
};

export default useCheckoutPage;
